import { useState, useMemo, useCallback } from "react";
import isNil from "lodash/isNil";
import { Box, Card, CardContent, Typography, Alert } from "@mui/material";
import { Button, Tooltip } from "@covertree/ui";
import {
  AdditionalInterestCard,
  countInterestsByType,
} from "@covertree/features";
import { PolicyUsage } from "@covertree/new-business-graphql";

import { AdditionalInterestModal } from "./AdditionalInterestModal";
import { AdditionalInterestRemovalModal } from "./AdditionalInterestRemovalModal";
import { useCheckout } from "../CheckoutProvider";
import { AdditionalInterestFormData } from "./form";
import {
  selectPolicyUsage,
  useNewBusinessSelector,
} from "@covertree/new-business-redux";

export function AdditionalInterestContainer() {
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedInterestIndex, setSelectedInterestIndex] = useState<
    number | null
  >(null);

  const isTenantFlow =
    useNewBusinessSelector(selectPolicyUsage) === PolicyUsage.Tenant;

  const {
    selectedPaymentPlanKey,
    anythingLoading,
    additionalInterests,
    setAdditionalInterests,
    hasLienholderError,
  } = useCheckout();

  const interestsCountByType = useMemo(
    () => countInterestsByType(additionalInterests),
    [additionalInterests]
  );

  const maxTotalAdditionalInterests = useMemo(() => {
    if (isTenantFlow) {
      return 7;
    }
    if (selectedPaymentPlanKey === "mortgageBill") {
      return 8;
    }
    return 9;
  }, [selectedPaymentPlanKey, isTenantFlow]);

  const handleAddInterest = useCallback(
    (data: AdditionalInterestFormData) => {
      setAdditionalInterests((prevAdditionalInterests) => {
        if (!isNil(selectedInterestIndex)) {
          prevAdditionalInterests[selectedInterestIndex] = data;
        } else {
          prevAdditionalInterests.push(data);
        }
        return [...prevAdditionalInterests];
      });
      setModalOpen(false);
      setSelectedInterestIndex(null);
    },
    [selectedInterestIndex]
  );

  const handleConfirmRemove = useCallback(() => {
    if (selectedInterestIndex !== null) {
      setAdditionalInterests((prevAdditionalInterests) =>
        prevAdditionalInterests.filter(
          (_, index) => index !== selectedInterestIndex
        )
      );
      setSelectedInterestIndex(null);
    }
    setConfirmDialogOpen(false);
  }, [selectedInterestIndex]);

  const handleEdit = (index: number) => {
    setSelectedInterestIndex(index);
    setModalOpen(true);
  };

  const handleRemove = (index: number) => {
    setSelectedInterestIndex(index);
    setConfirmDialogOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedInterestIndex(null);
  };

  const handleOpenModal = () => {
    setModalOpen(true);
    setSelectedInterestIndex(null);
  };

  return (
    <Box mt={3}>
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="h6" gutterBottom>
          Notify interested parties
        </Typography>
        <Tooltip
          dataTestId="additional-interest-tooltip"
          tooltip={
            "If you'd like another party to be notified about the policy but they are not responsible for payment, you can add them here for informational purposes only."
          }
          sx={{
            marginBottom: 1,
          }}
        />
      </Box>

      {hasLienholderError && (
        <Alert severity="error" sx={{ mb: 2, alignItems: "center" }}>
          Too many <strong>Lienholders</strong> added below. Please remove one
          of the lienholders before proceeding.
        </Alert>
      )}

      <Card>
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}>
          {!additionalInterests.length && (
            <Typography color="text.disabled">Nothing here yet</Typography>
          )}

          {additionalInterests.map((additionalInterest, index) => (
            <AdditionalInterestCard
              key={`additional-interest-${index}`}
              isCheckout={true}
              disabled={anythingLoading}
              additionalInterest={{
                ...additionalInterest,
                state: additionalInterest.state.value,
              }}
              actionMenuItems={[
                { label: "Edit", onClick: () => handleEdit(index) },
                { label: "Remove", onClick: () => handleRemove(index) },
              ]}
            />
          ))}

          {additionalInterests.length < maxTotalAdditionalInterests && (
            <Button
              variant="outlined"
              onClick={handleOpenModal}
              disabled={anythingLoading}>
              Add
            </Button>
          )}
        </CardContent>
      </Card>
      <AdditionalInterestModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSubmit={handleAddInterest}
        selectedInterest={
          !isNil(selectedInterestIndex)
            ? additionalInterests?.[selectedInterestIndex]
            : undefined
        }
        interestsCountByType={interestsCountByType}
      />

      <AdditionalInterestRemovalModal
        open={confirmDialogOpen}
        onConfirm={handleConfirmRemove}
        onCancel={() => setConfirmDialogOpen(false)}
        interestName={
          !isNil(selectedInterestIndex)
            ? additionalInterests?.[selectedInterestIndex]?.name
            : ""
        }
      />
    </Box>
  );
}
